import {
  CloseOutlined,
  PaperClipOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Input, Tooltip, Typography } from 'antd';
import { useCallback } from 'react';

import { formatBytes } from '@app/utils/formatBytes';

import type { SupportChatWindowModel } from '@features/SupportChat/types';
import type { ChangeEvent, FC, KeyboardEvent } from 'react';

const { TextArea } = Input;
const { Text } = Typography;

type SupportChatComposerProps = Pick<
  SupportChatWindowModel,
  | 'inputValue'
  | 'isSending'
  | 'isUnavailable'
  | 'attachments'
  | 'isReadingFiles'
  | 'acceptedFileTypes'
  | 'fileInputRef'
  | 'handleInputChange'
  | 'handleSend'
  | 'handleAttachClick'
  | 'handleFilesSelected'
  | 'handleRemoveAttachment'
>;

export const SupportChatComposer: FC<SupportChatComposerProps> = ({
  inputValue,
  isSending,
  isUnavailable,
  attachments,
  isReadingFiles,
  acceptedFileTypes,
  fileInputRef,
  handleInputChange,
  handleSend,
  handleAttachClick,
  handleFilesSelected,
  handleRemoveAttachment,
}) => {
  const onChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
      handleInputChange(value);
    },
    [handleInputChange]
  );

  const onPressEnter = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.shiftKey) return;

      event.preventDefault();
      void handleSend();
    },
    [handleSend]
  );

  const hasContent = Boolean(inputValue.trim()) || attachments.length > 0;

  return (
    <div className="SupportChatComposer">
      {attachments.length > 0 && (
        <div className="SupportChatComposer__attachments">
          {attachments.map(({ id, name, size }) => (
            <div key={id} className="SupportChatComposer__attachment">
              <PaperClipOutlined className="SupportChatComposer__attachment-icon" />
              <Text
                className="SupportChatComposer__attachment-name"
                ellipsis={{ tooltip: name }}
              >
                {name}
              </Text>
              <Text
                type="secondary"
                className="SupportChatComposer__attachment-size"
              >
                {formatBytes(size)}
              </Text>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                aria-label={`Убрать файл ${name}`}
                disabled={isSending}
                onClick={() => handleRemoveAttachment(id)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="SupportChatComposer__row">
        <Tooltip title="Прикрепить файл">
          <Button
            type="text"
            shape="circle"
            icon={<PaperClipOutlined />}
            aria-label="Прикрепить файл"
            loading={isReadingFiles}
            disabled={isUnavailable || isSending}
            onClick={handleAttachClick}
          />
        </Tooltip>
        <input
          ref={fileInputRef}
          className="SupportChatComposer__file-input"
          type="file"
          multiple
          accept={acceptedFileTypes}
          onChange={handleFilesSelected}
        />
        <TextArea
          value={inputValue}
          onChange={onChange}
          onPressEnter={onPressEnter}
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={isUnavailable}
          placeholder="Сообщение..."
        />
        <Button
          type="primary"
          shape="circle"
          icon={<SendOutlined />}
          aria-label="Отправить"
          loading={isSending}
          disabled={isUnavailable || isReadingFiles || !hasContent}
          onClick={handleSend}
        />
      </div>
    </div>
  );
};
